import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useRef } from 'react';
import { rootRoute } from './__root';
import { useAuth } from '@/hooks/useAuth';
import { GlassCard } from '@/components/ui/glass-card';
import { getOAuthErrorMessage } from '@/lib/authErrors';
import { redirectDesktopOAuthIfNeeded } from '@/lib/oauthCallback';

export const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
});

// 後方互換性のため
export const Route = authCallbackRoute;

function AuthCallbackPage() {
  const { handleOAuthCallback } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const isProcessing = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      // Prevent double execution (React StrictMode)
      if (isProcessing.current) return;
      isProcessing.current = true;

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const errorParam = params.get('error');

      if (errorParam) {
        setError(getOAuthErrorMessage(errorParam));
        return;
      }

      if (!code || !state) {
        setError('Missing authorization code or state');
        return;
      }

      try {
        const redirected = await redirectDesktopOAuthIfNeeded(code, state);
        if (redirected) {
          return;
        }

        await handleOAuthCallback(code, state);
        navigate({ to: '/gallery' });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    processCallback();
  }, [handleOAuthCallback, navigate]);

  if (error) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background p-4"
        data-testid="auth-callback-error"
      >
        <GlassCard className="w-full max-w-md p-8 text-center" intensity="medium">
          <h1 className="mb-4 text-2xl font-bold text-white">Authentication Failed</h1>
          <p className="mb-6 text-white/80">{error}</p>
          <a
            href="/login"
            className="inline-block rounded-lg bg-white px-6 py-2 font-medium text-foreground hover:bg-muted"
          >
            Back to Login
          </a>
        </GlassCard>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background"
      data-testid="auth-callback-loading"
    >
      <div className="text-center">
        <div
          className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent"
          role="status"
          aria-label="Processing authentication"
        />
        <p className="text-lg font-medium text-white">Authenticating...</p>
      </div>
    </div>
  );
}
